import "./Orders.css";
import React, { useContext, useEffect, useState } from "react";
import { Card, Col, Row, Spin, Switch, Tooltip } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { HomeOutlined, ReconciliationOutlined } from "@ant-design/icons";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import PendingOrders from "./PendingOrders";
import {
  TransactionStatus,
  UserType,
} from "../../../configs/constants";
import InprogressOrders from "./InprogressOrders";
import CompletedOrders from "./CompletedOrders";
import CancelledOrders from "./CancelledOrders";
import { getBranches, getOrdersCount } from "../../../network/network";
import { AppContext } from "../../../context/AppContextProvider";
import { handleApiError } from "../../../utils/errorHandler";

const Orders = () => {
  const { authUser } = useContext(AppContext);
  const [reload, setReload] = useState(false);
  const [tickets, setTickets] = useState([]);
  const { t } = useTranslation();
  const [ordersCount, setOrdersCount] = useState([]);
  const [branchesData, setBranchesData] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isAllData, setIsAllData] = useState(false);

  useEffect(() => {
    setIsDataLoading(true);
    getBranches({ per_page: -1 })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setBranchesData(response.data.data.branches);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [t]);

  useEffect(() => {
    setIsDataLoading(true);
    getOrdersCount({
      is_all_data: isAllData,
    })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setOrdersCount(response.data.data);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [reload, isAllData, t]);

  const onChangeTab = (key) => {
    setTickets(key);
  };

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "orders",
      breadcrumbName: t("orders"),
      icon: <ReconciliationOutlined />,
    },
  ];

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("orders")}
          ghost={false}
          extra={[
            authUser.type === UserType.BusinessOwner && (
              <Tooltip
                placement="topLeft"
                title={
                  isAllData
                    ? t("Showing_orders_from_all_branches")
                    : t("Showing_orders_from_my_branch")
                }
              >
                <Switch
                  checked={isAllData}
                  onChange={(checked) => {
                    setIsAllData(checked);
                  }}
                  checkedChildren={t("all_data")}
                  unCheckedChildren={t("all_data")}
                />
              </Tooltip>
            ),
          ]}
        />
      </Card>
      <Spin spinning={isDataLoading}>
        <Row
          gutter={[20, 20]}
          style={{ marginBottom: "20px", textAlign: "center" }}
        >
          <Col span={6}>
            <Card
              className={
                "tab_title" +
                (tickets === TransactionStatus.Pending ? " active" : "")
              }
              onClick={() => onChangeTab(TransactionStatus.Pending)}
            >
              <div>
                {t("pending_orders")}
                <span className="tab_count">
                  {ordersCount.pending_orders_count}
                </span>
              </div>
            </Card>
          </Col>
          <Col span={6}>
            <Card
              className={
                "tab_title" +
                (tickets === TransactionStatus.InProgress ? " active" : "")
              }
              onClick={() => onChangeTab(TransactionStatus.InProgress)}
            >
              <div>
                {t("inprogress_orders")}
                <span className="tab_count">
                  {ordersCount.in_progress_orders_count}
                </span>
              </div>
            </Card>
          </Col>
          <Col span={6}>
            <Card
              className={
                "tab_title" +
                (tickets === TransactionStatus.Completed ? " active" : "")
              }
              onClick={() => onChangeTab(TransactionStatus.Completed)}
            >
              <div>
                {t("completed_orders")}
                <span className="tab_count">
                  {ordersCount.completed_orders_count}
                </span>
              </div>
            </Card>
          </Col>
          <Col span={6}>
            <Card
              className={
                "tab_title" +
                (tickets === TransactionStatus.Cancelled ? " active" : "")
              }
              onClick={() => onChangeTab(TransactionStatus.Cancelled)}
            >
              <div>
                {t("cancelled_orders")}
                <span className="tab_count">
                  {ordersCount.cancelled_orders_count}
                </span>
              </div>
            </Card>
          </Col>
        </Row>
      </Spin>
      {tickets === TransactionStatus.Pending && (
        <PendingOrders
          branchesData={branchesData}
          reloadCallback={() => setReload(!reload)}
          isAllData={isAllData}
        />
      )}
      {tickets === TransactionStatus.InProgress && (
        <InprogressOrders
          branchesData={branchesData}
          reloadCallback={() => setReload(!reload)}
          isAllData={isAllData}
        />
      )}
      {tickets === TransactionStatus.Completed && (
        <CompletedOrders
          branchesData={branchesData}
          reloadCallback={() => setReload(!reload)}
          isAllData={isAllData}
        />
      )}
      {tickets === TransactionStatus.Cancelled && (
        <CancelledOrders
          branchesData={branchesData}
          reloadCallback={() => setReload(!reload)}
          isAllData={isAllData}
        />
      )}
    </>
  );
};

export default Orders;
